import React, { useEffect, useState, useCallback } from 'react';
import Table from '../../../../../../../../Template/layoutComponents/Table';
import { notification } from 'antd';
import { useMsal } from '@azure/msal-react';
import { deleteFile, getFiles, saveFile } from '../../../../../../../helpers/files';
import useTemplateState from '../../../../../../../../Template/atoms/variables';
import { getTableConfig } from '../../../helpers/tableConfigHelper';
import moment from 'moment';
import { getOrCreateTask, updateTaskNoValidation } from '../../../../../../../helpers/apiCalls';
import { useParams } from 'react-router-dom';
import BypassButton from '../../helperComponents/BypassButton';

import { useTranslation } from 'react-i18next';

const DocumentUpload = ({
  asset,
  phaseTitle,
  stage,
  stageIndex,
  type,
  setStepProgress,
  testId,
  setAllTasks,
  isLocked,
  setRefetchIssues,
  issues,
}) => {
  const { setPendingApiCalls } = useTemplateState();
  const { instance } = useMsal();
  const { notificationApi, contextHolder } = notification.useNotification();
  const { assetId, interconnectionId } = useParams();
  const { i18n, t } = useTranslation();

  const [documents, setDocuments] = useState([]);
  const [itemsByLanguage, setItemsByLanguage] = useState({});
  const [allMandatoryUploaded, setAllMandatoryUploaded] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [systemTask, setSystemTask] = useState(null);

  const LABELS = {
    docUpload: t('doc_upload'),
    bypassed: 'bypassed',
    validationResults: t('validation_results'),
    field: t('field'),
    expected: t('expected'),
    found: t('found'),
    match: t('match'),
  };

  const handleError = (msg, desc) =>
    notificationApi?.error({ message: msg, description: desc, duration: 5 });

  const getTaskStatus = (issues, hasFiles = false) => {
    if (!hasFiles) return 'Pending';

    return issues.items?.length === 0 ? 'Validated' : 'Pending';
  };

  const fetchDocuments = useCallback(async () => {
    try {
      const resp = await getFiles(instance, type, asset._id, true);
      console.log('Fetched documents:', resp);
      setDocuments(
        resp.map((d) => ({
          id: d.name,
          documentName: d.name.split('/').pop() || '-',
          uploadedBy: d.metadata?.userid || '-',
          url: d.url || '',
          dateTime: d.lastModified ? moment(d.lastModified).format('DD/MM/YYYY HH:mm') : '-',
        })),
      );
    } catch (err) {
      handleError('Failed to fetch documents', err?.message);
    }
  }, [instance, type, asset]);

  const fetchSystemTask = useCallback(async () => {
    try {
      setPendingApiCalls((p) => p.concat(['getOrCreateTask']));
      const task = await getOrCreateTask(instance, {
        test: testId,
        stage: phaseTitle,
        subjectId: assetId ?? interconnectionId,
        subjectType: assetId ? 'Asset' : 'Interconnection',
        token: `${assetId ?? interconnectionId}-${phaseTitle}-FILEUP-${stageIndex}`,
        code: 'FILEUP',
      });
      setSystemTask(task);
    } catch (err) {
      handleError('Failed to fetch task', err?.message);
    } finally {
      setPendingApiCalls((p) => p.filter((c) => c !== 'getOrCreateTask'));
    }
  }, [instance, testId, phaseTitle, assetId, interconnectionId, stageIndex]);

  const handleUpload = useCallback(async () => {
    await fetchDocuments();
    await fetchSystemTask();
  }, [fetchDocuments, fetchSystemTask]);

  useEffect(() => {
    if (!asset) return;
    (async () => {
      setTableLoading(true);
      try {
        buildTableItems();
        await fetchDocuments();
        await fetchSystemTask();
      } finally {
        setTableLoading(false);
      }
    })();
  }, [asset, fetchDocuments, fetchSystemTask]);

  const buildTableItems = () => {
    const languages = ['pt', 'en', 'es'];
    setItemsByLanguage(buildItemsByLanguage(languages));
  };

  const buildItemsByLanguage = (languages) => {
    const reduce = languages.reduce((items, lang) => {
      items[lang] = stage
        ? stage[lang]?.plan?.steps[0]?.items.map((item) => ({
            id: item._id,
            documentType: item.type,
            mandatoryItem: item.mandatory,
            shouldValidate: item.shouldValidate,
            validateItems: [...item.validateItems, item?.customPrompt].filter(Boolean),
          })) || []
        : [];
      return items;
    }, {});

    return reduce;
  };

  const getItemsByLanguage = () => itemsByLanguage[i18n.language] || [];

  const getUploadedItems = (items) =>
    items.filter((_, idx) =>
      documents.some((d) =>
        d.documentName.startsWith(`phase_${phaseTitle}_${stageIndex}_index_${idx}_`),
      ),
    );

  const getMandatoryItems = (items) => items.filter((it) => it.mandatoryItem);

  const calculateProgress = () => {
    const items = getItemsByLanguage();
    const uploadedItems = getUploadedItems(items);
    const mandatoryItems = getMandatoryItems(getItemsByLanguage());

    if (mandatoryItems.length === 0) {
      setAllMandatoryUploaded(true);

      return items.length > 0 ? Math.round((uploadedItems.length / items.length) * 100) : 0;
    } else {
      setAllMandatoryUploaded(
        uploadedItems.filter((up) => mandatoryItems.some((mand) => mand.id === up.id)).length ===
          mandatoryItems.length,
      );

      return mandatoryItems.length > 0
        ? Math.round(
            (uploadedItems.filter((up) => mandatoryItems.some((mand) => mand.id === up.id)).length /
              mandatoryItems.length) *
              100,
          )
        : 0;
    }
  };

  const mergeDataForTable = () => {
    const items = getItemsByLanguage();
    return items.map((item, index) => {
      const match = documents.find((d) =>
        d.documentName.startsWith(`phase_${phaseTitle}_${stageIndex}_index_${index}_`),
      );
      return {
        ...item,
        index,
        documentName: match?.documentName.split('_').slice(5).join('_') || '-',
        uploadedBy: match?.uploadedBy || '-',
        dateTime: match?.dateTime || '-',
        fileUrl: match?.url || '',
        status: systemTask?.status || 'Pending',
        manage: 'placeholder',
      };
    });
  };

  useEffect(() => {
    if (!systemTask) return;

    if (systemTask.bypass?.bypassed) {
      setAllTasks((p) => [
        ...p?.filter((t) => t.test._id !== `${testId}-${stageIndex}`),
        { test: { _id: `${testId}-${stageIndex}` }, report: { status: 'approved' } },
      ]);
      return;
    }

    const progress = calculateProgress();
    setStepProgress((c) => ({ ...c, [phaseTitle]: progress }));
    setAllTasks((p) => [
      ...p?.filter((t) => t.test._id !== `${testId}-${stageIndex}`),
      {
        test: { _id: `${testId}-${stageIndex}` },
        report: { status: allMandatoryUploaded ? 'approved' : 'pending' },
      },
    ]);
  }, [
    documents,
    itemsByLanguage,
    allMandatoryUploaded,
    systemTask,
    instance,
    setAllTasks,
    setStepProgress,
    phaseTitle,
    testId,
    stageIndex,
  ]);

  useEffect(() => {
    if (!systemTask) return;

    const hasFiles = documents.length > 0;
    const newStatus = getTaskStatus(issues, hasFiles);
    if (newStatus !== systemTask.status) {
      setSystemTask((prev) => ({ ...prev, status: newStatus }));
      updateTaskNoValidation(instance, { _id: systemTask._id, status: newStatus });
    }
  }, [issues.items, systemTask, instance, documents]);

  const deleteDocument = async (row) => {
    const match = documents.find((d) =>
      d.documentName.startsWith(`phase_${phaseTitle}_${stageIndex}_index_${row.index}_`),
    );
    if (!match) {
      return handleError('Document not found', 'No matching document found.');
    }

    setPendingApiCalls((p) => [...p, 'deleteFile']);
    try {
      await deleteFile(instance, type, asset._id, match.id, systemTask?._id, phaseTitle);
      setRefetchIssues(true);
      await fetchDocuments();
    } catch (err) {
      handleError('Failed to delete document', err.message);
    } finally {
      setPendingApiCalls((p) => p.filter((c) => c !== 'deleteFile'));
    }
  };

  const getValidationItems = () =>
    systemTask?.validationResults?.map((v, i) => ({
      key: i,
      field: v.field,
      expected: v.expected_value,
      found: v.found_value,
      match: v.match ? '✅' : '❌',
    })) || [];

  const validationTableSettings = {
    columnsConfig: {
      field: { label: LABELS.field },
      expected: { label: LABELS.expected },
      found: { label: LABELS.found },
      match: { label: LABELS.match, align: 'center' },
    },
    pagination: false,
    bordered: true,
  };

  const mainTableSettings = getTableConfig({
    t,
    isLocked: isLocked || systemTask?.bypass?.bypassed,
    deleteDocument,
    fetchDocuments: async () => {
      setTableLoading(true);
      await fetchDocuments();
      setTableLoading(false);
    },
    asset,
    type,

    saveFile: async (...args) => {
      const hasFiles = documents.length > 0;
      const nextStatus = getTaskStatus(issues, hasFiles);
      setSystemTask((prev) => (prev ? { ...prev, status: nextStatus } : prev));

      const resp = await saveFile(...args);

      await handleUpload();

      const hasFilesAfterUpload = documents.length > 0 || resp;
      await updateTaskNoValidation(instance, {
        _id: systemTask?._id ?? resp?.taskId,
        status: getTaskStatus(issues, hasFilesAfterUpload),
      });

      return resp;
    },
    phaseTitle,
    stageIndex,
    setRefetchIssues,
    systemTask,
  });

  return (
    <>
      {contextHolder}
      <div style={{ height: '100%', width: '100%' }}>
        {/* cabeçalho */}
        <div className='tasksContentHeader'>
          <div className='bypassedContainer'>
            <div className={'pageTitle' + (systemTask?.bypass?.bypassed ? ' bypassed' : '')}>
              {t('document_upload_title')}
            </div>
            {systemTask?.bypass?.bypassed && (
              <div className='bypassedText'>{`(${LABELS.bypassed})`}</div>
            )}
          </div>
          <div className='reportTableButtons'>
            <BypassButton
              systemTask={{
                ...systemTask,
                status: documents.length > 0 && allMandatoryUploaded ? 'Validated' : 'Pending',
              }}
              title={LABELS.docUpload}
              bypassCallback={() => {
                setAllTasks((p) => [
                  ...p?.filter((t) => t.test._id !== `${testId}-${stageIndex}`),
                  {
                    test: { _id: `${testId}-${stageIndex}` },
                    report: { status: 'approved' },
                  },
                ]);
                setSystemTask((prev) => ({
                  ...prev,
                  bypass: { bypassed: true },
                }));
              }}
            />
          </div>
        </div>

        <Table
          items={mergeDataForTable()}
          handleSelect={null}
          handleRowClick={null}
          startingTotalItems={mergeDataForTable().length}
          tableSettings={mainTableSettings}
          apiCall={async () => ({
            items: mergeDataForTable(),
            totalItems: mergeDataForTable().length,
          })}
          loadingSkeleton={tableLoading}
        />

        {systemTask?.validationResults?.length > 0 && (
          <div style={{ marginTop: '80px' }}>
            <div
              style={{
                fontSize: '28px',
                fontWeight: '600',
                marginBottom: '10px',
              }}
            >
              {LABELS.validationResults}
            </div>
            <Table
              items={getValidationItems()}
              handleSelect={null}
              handleRowClick={null}
              startingTotalItems={getValidationItems().length}
              tableSettings={validationTableSettings}
              apiCall={async () => ({
                items: getValidationItems(),
                totalItems: getValidationItems().length,
              })}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DocumentUpload;
